/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { ReactNode, useEffect } from 'react';
import { isRecaptchaDisabled, isRecaptchaConfigured } from '@/shared/utils/recaptcha';
import { ENVIRONMENT } from '@/shared/utils/environment';
import { reportAntiBotError } from '@/context/AntiBotContext';

interface ReCaptchaProviderProps {
  children: ReactNode;
}

/**
 * ReCaptchaProvider component.
 *
 * Validates anti-bot configuration and renders its children. It deliberately does
 * NOT load the Enterprise script: the SDK is injected on demand by
 * `ensureRecaptchaScript()` the first time a token is needed, so the public
 * landing page -- which has no form -- never downloads it.
 *
 * This component used to inject the script itself, duplicating an inline block in
 * index.html while reproducing none of its guards, so an environment with an inert
 * site key still reached google.com. Configuration validation and the
 * MisconfiguredGuard below remain this component's job.
 */
const ReCaptchaProvider: React.FC<ReCaptchaProviderProps> = ({ children }) => {
  if (isRecaptchaDisabled()) {
    if (import.meta.env.DEV) {
      console.info(
        'ReCaptchaProvider: reCAPTCHA is disabled (development/debug mode). Set REACT_APP_DISABLE_RECAPTCHA=false to enable.'
      );
    }
    return <>{children}</>;
  }

  if (!isRecaptchaConfigured()) {
    if (ENVIRONMENT !== 'local') {
      return <MisconfiguredGuard>{children}</MisconfiguredGuard>;
    }
    if (import.meta.env.DEV) {
      console.warn(
        'ReCaptchaProvider: ANTI_BOT_PROVIDER_ID is not set. reCAPTCHA protection is disabled.'
      );
    }
    return <>{children}</>;
  }

  return <>{children}</>;
};

/**
 * Renders children but fires a global anti-bot error on mount so the
 * AntiBotErrorOverlay covers the page.
 */
function MisconfiguredGuard({ children }: { children: ReactNode }) {
  useEffect(() => {
    reportAntiBotError(
      new Error('ANTI_BOT_PROVIDER_ID is not configured — anti-bot protection is unavailable'),
      0,
    );
  }, []);

  return <>{children}</>;
}

export default ReCaptchaProvider;
